import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import {
  createOrGetSupportThread,
  listenToSupportMessages,
  listenToAvailableAgents,
  sendSupportMessage,
  addOptimisticMessage,
} from "../../redux/supportSlice";
import { Send, Headphones, Sparkles, Mail, X, Info } from "lucide-react";

// ── Design tokens ─────────────────────────────────────────────
const T = {
  bg:        "#FFF9F0",
  bgPage:    "#1A1208",
  bgInput:   "#F0E8D8",
  bgMeGrad:  "linear-gradient(135deg,#C8860A,#8B0000)",
  border:    "#EAD7B8",
  gold:      "#C8860A",
  obsidian:  "#1A1208",
  text:      "#1A1208",
  textMuted: "#8B6A3A",
  textLight: "#B89060",
  online:    "#22C55E",
  away:      "#F59E0B",
  white:     "#FFF9F0",
  ring1:     "#C8860A",
  ring2:     "#A0000A",
  ring3:     "#1A1208",
};

// ── Auto-reply endpoint ───────────────────────────────────────
const AUTO_REPLY_URL =
  "https://europe-west3-alreluxe.cloudfunctions.net/autoReply";

// ── Email modal ───────────────────────────────────────────────
const EmailModal: React.FC<{ agentName: string; onClose: () => void }> = ({
  agentName,
  onClose,
}) => {
  const [subject, setSubject] = useState("");
  const [body,    setBody]    = useState("");
  const [sent,    setSent]    = useState(false);

  const handleSend = () => {
    if (!subject.trim() || !body.trim()) return;
    const mailto =
      `mailto:support@yourcompany.com` +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;
    window.open(mailto, "_blank");
    setSent(true);
    setTimeout(onClose, 1800);
  };

  return (
    <div style={M.backdrop} onClick={onClose}>
      <div style={M.card} onClick={e => e.stopPropagation()}>
        {sent ? (
          <div style={M.sentWrap}>
            <div style={M.sentIconWrap}>
              <Mail size={28} color={T.gold} />
            </div>
            <div style={M.sentTitle}>Email opened!</div>
            <div style={M.sentSub}>We'll get back to you shortly.</div>
          </div>
        ) : (
          <>
            <div style={M.header}>
              <div style={M.headerLeft}>
                <div style={M.mailIconWrap}>
                  <Mail size={20} color={T.gold} />
                </div>
                <div>
                  <div style={M.title}>Send an email</div>
                  <div style={M.sub}>to {agentName} & the team</div>
                </div>
              </div>
              <button style={M.closeBtn} onClick={onClose}>
                <X size={14} color="#6B5533" />
              </button>
            </div>

            <div style={M.field}>
              <label style={M.label}>Subject</label>
              <input
                style={M.input}
                placeholder="What's on your mind?"
                value={subject}
                onChange={e => setSubject(e.target.value)}
              />
            </div>

            <div style={M.field}>
              <label style={M.label}>Message</label>
              <textarea
                style={M.textarea}
                placeholder={`Hi ${agentName}, I need help with…`}
                value={body}
                onChange={e => setBody(e.target.value)}
                rows={5}
              />
            </div>

            <button
              style={{
                ...M.sendBtn,
                opacity: subject.trim() && body.trim() ? 1 : 0.45,
                cursor:  subject.trim() && body.trim() ? "pointer" : "default",
              }}
              onClick={handleSend}
              disabled={!subject.trim() || !body.trim()}
            >
              <span>Send email</span>
              <Mail size={15} color={T.white} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────
const CustomerSupport: React.FC = () => {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();

  const { user } = useSelector((s: RootState) => s.auth);
  const { thread, messages, loading, availableAgents } =
    useSelector((s: RootState) => s.support);

  const [text,        setText]        = useState("");
  const [autoWelcome, setAutoWelcome] = useState("");
  const [agentTyping, setAgentTyping] = useState(false);
  const [botTyping,   setBotTyping]   = useState(false); // ← bot auto-reply indicator
  const [welcomed,    setWelcomed]    = useState(false);
  const [showEmail,   setShowEmail]   = useState(false);

  const chatRef       = useRef<HTMLDivElement>(null);
  const inputRef      = useRef<HTMLInputElement>(null);
  const currentUserId = user?.userId ?? "";
  const userFirstName = user?.firstName ?? "there";

  // ── Agent resolution ──────────────────────────────────────
  const assignedAgent = thread
    ? availableAgents.find((a) => a.id === thread.agentId) ?? null
    : null;
  const displayAgent  = assignedAgent ?? availableAgents[0] ?? null;
  const agentName     = displayAgent
    ? `${displayAgent.firstName} ${displayAgent.lastName}`.trim() || "Support"
    : "Support";
  const agentPhoto    = displayAgent?.profilePicture ?? null;
  const agentOnline   = availableAgents.length > 0;
  const threadLoading = loading && !thread;
  const canSend       = !!text.trim() && !threadLoading && !botTyping;

  // ── Effects ───────────────────────────────────────────────
  useEffect(() => { dispatch(listenToAvailableAgents()); }, [dispatch]);

  useEffect(() => {
    if (!currentUserId) return;
    dispatch(createOrGetSupportThread())
      .unwrap()
      .then((t) => dispatch(listenToSupportMessages({ threadId: t.id })))
      .catch((err) => console.error("[CustomerSupport] thread init failed:", err));
  }, [dispatch, currentUserId]);

  useEffect(() => {
    if (welcomed || !displayAgent) return;
    setWelcomed(true);
    const greeting = displayAgent.firstName || "Support";
    const welcome  = `Hey ${userFirstName}! I'm ${greeting}, how can I help you today?`;
    let i = 0;
    const iv = setInterval(() => {
      setAutoWelcome(welcome.slice(0, i));
      i++;
      if (i > welcome.length) {
        clearInterval(iv);
        setTimeout(() => {
          setAgentTyping(true);
          setTimeout(() => setAgentTyping(false), 2000);
        }, 300);
      }
    }, 26);
    return () => clearInterval(iv);
  }, [displayAgent, welcomed, userFirstName]);

  // hide bot typing indicator when a new bot message arrives
  useEffect(() => {
    if (botTyping && messages.length > 0) {
      const last = messages[messages.length - 1];
      // bot messages have senderId === "bot_support_agent"
      if (last.senderId === "bot_support_agent") {
        setBotTyping(false);
      }
    }
  }, [messages, botTyping]);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (chatRef.current)
        chatRef.current.scrollTop = chatRef.current.scrollHeight;
    });
  }, [messages, autoWelcome, agentTyping, botTyping]);

  // ── Send ──────────────────────────────────────────────────
  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || !thread || !currentUserId) return;

    const optimisticId = `opt_${Date.now()}`;

    // 1. Optimistic message in UI
    dispatch(addOptimisticMessage({
      id:                   optimisticId,
      senderId:             currentUserId,
      senderName:           user?.firstName ?? "You",
      senderProfilePicture: user?.profilePicture ?? null,
      text:                 trimmed,
      timestamp:            Date.now(),
      status:               "sending",
      optimistic:           true,
    }));

    setText("");
    inputRef.current?.focus();

    // 2. Write message to Firestore (supportSlice thunk)
    try {
      await dispatch(sendSupportMessage({ text: trimmed, optimisticId })).unwrap();
    } catch (err: any) {
      console.error("[handleSend] sendSupportMessage failed:", err);
      return; // don't attempt auto-reply if send failed
    }

    // 3. Show bot typing indicator while waiting for auto-reply
    setBotTyping(true);

    // 4. Call autoReply — fire and forget (Firestore listener will
    //    surface the bot message automatically via listenToSupportMessages)
    try {
      const res = await fetch(AUTO_REPLY_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({
          threadId:    thread.id,
          userId:      currentUserId,
          messageText: trimmed,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error("[autoReply] HTTP error:", res.status, err);
      }
    } catch (err: any) {
      console.error("[autoReply] fetch failed:", err.message);
    } finally {
      // Safety fallback: hide typing indicator after 8 s even if
      // something went wrong and no bot message arrives
      setTimeout(() => setBotTyping(false), 8000);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const formatTime = (ts: number | null) => {
    if (!ts) return "";
    return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // ── Spinning ring avatar ──────────────────────────────────
  const AgentAvatar: React.FC<{ size?: number }> = ({ size = 36 }) => {
    const pad       = size > 34 ? 3 : 2;
    const outerSize = size + pad * 2 + 2;
    const iconSize  = Math.round(size * 0.42);
    return (
      <div style={{ position: "relative", flexShrink: 0, width: outerSize, height: outerSize }}>
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          background: `conic-gradient(from 0deg,${T.ring1},${T.ring2},${T.ring3},${T.ring1})`,
          animation: "ringSpinCS 2.8s linear infinite",
        }} />
        <div style={{ position: "absolute", inset: pad - 1, borderRadius: "50%", background: T.bg }} />
        <div style={{
          position: "absolute", inset: pad + 1, borderRadius: "50%",
          background: T.obsidian, overflow: "hidden",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {agentPhoto
            ? <img src={agentPhoto} alt={agentName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <Headphones size={iconSize} color={T.gold} />}
        </div>
        <div style={{
          position: "absolute", bottom: 1, right: 1,
          width: size > 38 ? 11 : 8, height: size > 38 ? 11 : 8,
          borderRadius: "50%",
          backgroundColor: agentOnline ? T.online : T.away,
          border: `2px solid ${T.bg}`, zIndex: 4,
        }} />
      </div>
    );
  };

  const TypingDots = () => (
    <div style={S.typingBubble}>
      {[0, 1, 2].map(i => (
        <div key={i} className="cs-dot" style={{ animationDelay: `${i * 0.16}s` }} />
      ))}
    </div>
  );

  return (
    <div style={S.page} className="cs-page">
      <style>{CSS}</style>

      {showEmail && (
        <EmailModal agentName={agentName} onClose={() => setShowEmail(false)} />
      )}

      <div style={S.shell} className="cs-shell">

        {/* ── Header ── */}
        <div style={S.header}>
          <button
            style={S.iconBtn}
            onClick={() => setShowEmail(true)}
            title="Send us an email"
          >
            <Mail size={17} color={T.textMuted} />
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
            <AgentAvatar size={42} />
            <div>
              <div style={S.headerName}>{agentName}</div>
              <div style={S.headerSub}>
                <span style={{
                  display: "inline-block", width: 6, height: 6, borderRadius: "50%",
                  backgroundColor: agentOnline ? T.online : T.away,
                  marginRight: 5, flexShrink: 0,
                }} />
                {threadLoading
                  ? "Connecting…"
                  : agentOnline
                    ? "Online · here to help"
                    : "Away · we'll get back to you"}
              </div>
            </div>
          </div>

          <button style={S.iconBtn} title="Support info">
            <Info size={17} color={T.textMuted} />
          </button>
        </div>

        {/* ── Greeting banner ── */}
        {!autoWelcome && !messages.length && (
          <div style={S.greetBanner}>
            <Sparkles size={14} color={T.gold} style={{ flexShrink: 0 }} />
            <span>We usually reply within a few minutes!</span>
          </div>
        )}

        {/* ── Date pill ── */}
        <div style={S.datePill}>Today</div>

        {/* ── Messages ── */}
        <div ref={chatRef} style={S.messagesArea}>

          {/* Welcome typewriter */}
          {autoWelcome && (
            <div style={S.rowLeft} className="cs-msg-in">
              <AgentAvatar size={28} />
              <div>
                <div style={S.senderLabel}>{displayAgent?.firstName || "Support"}</div>
                <div style={S.bubbleOther}>
                  <span style={{ ...S.bubbleText, color: T.text }}>{autoWelcome}</span>
                  {autoWelcome.length > 5 && (
                    <span style={{ ...S.ts, color: T.textLight }}>now</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Agent typing (welcome animation) */}
          {agentTyping && (
            <div style={S.rowLeft} className="cs-msg-in">
              <AgentAvatar size={28} />
              <div>
                <div style={S.senderLabel}>{displayAgent?.firstName || "Support"}</div>
                <TypingDots />
              </div>
            </div>
          )}

          {/* Conversation messages */}
          {messages.map((msg) => {
            const isMe      = msg.senderId === currentUserId;
            const isBot     = msg.senderId === "bot_support_agent";
            const isFailed  = msg.status === "failed";
            const isPending = msg.status === "sending";

            return (
              <div
                key={msg.id}
                style={isMe ? S.rowRight : S.rowLeft}
                className={isMe ? "cs-msg-out" : "cs-msg-in"}
              >
                {!isMe && <AgentAvatar size={28} />}
                <div style={{ maxWidth: "100%" }}>
                  {!isMe && (
                    <div style={S.senderLabel}>
                      {isBot ? "Areluxe Support" : (msg.senderName || agentName)}
                    </div>
                  )}
                  <div style={{
                    ...(isMe ? S.bubbleMe : S.bubbleOther),
                    opacity: isPending ? 0.65 : 1,
                    ...(isFailed ? { background: "#FFF1F2", border: "1px solid #FECDD3" } : {}),
                  }}>
                    <span style={{
                      ...S.bubbleText,
                      color: isMe ? T.white : isFailed ? "#9F1239" : T.text,
                    }}>
                      {msg.text}
                    </span>
                    <span style={{
                      ...S.ts,
                      color: isMe ? "rgba(255,245,230,0.55)" : T.textLight,
                    }}>
                      {isFailed ? "Failed to send" : isPending ? "Sending…" : formatTime(msg.timestamp)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Bot typing indicator — shown while autoReply is in flight ── */}
          {botTyping && (
            <div style={S.rowLeft} className="cs-msg-in">
              <AgentAvatar size={28} />
              <div>
                <div style={S.senderLabel}>Areluxe Support</div>
                <TypingDots />
              </div>
            </div>
          )}

        </div>

        {/* ── Input bar ── */}
        <div style={S.inputBar}>
          <div style={S.inputWrap}>
            <input
              ref={inputRef}
              style={S.input}
              placeholder={
                botTyping
                  ? "Support is replying…"
                  : `Message ${displayAgent?.firstName || "Support"}…`
              }
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={handleKey}
              disabled={botTyping}
            />
          </div>
          <button
            style={{
              ...S.sendBtn,
              background:  canSend ? T.bgMeGrad : T.bgInput,
              boxShadow:   canSend ? "0 4px 16px rgba(200,134,10,0.4)" : "none",
              cursor:      canSend ? "pointer" : "default",
              transform:   canSend ? "scale(1)" : "scale(0.94)",
            }}
            onClick={handleSend}
            disabled={!canSend}
          >
            <Send size={17} color={canSend ? T.white : T.textLight} />
          </button>
        </div>

        {/* ── Footer ── */}
        <div style={S.poweredBy}>
          <Sparkles size={11} color={T.textLight} />
          <span>Powered by your support team</span>
        </div>

      </div>
    </div>
  );
};

export default CustomerSupport;

// ── Modal styles ──────────────────────────────────────────────
const M: Record<string, React.CSSProperties> = {
  backdrop: {
    position: "fixed", inset: 0, zIndex: 50,
    backgroundColor: "rgba(10,6,2,0.6)",
    backdropFilter: "blur(6px)",
    display: "flex", alignItems: "flex-end", justifyContent: "center",
  },
  card: {
    width: "100%", maxWidth: 480,
    backgroundColor: "#FFF9F0",
    borderRadius: "24px 24px 0 0",
    padding: "28px 24px 36px",
    boxShadow: "0 -8px 48px rgba(0,0,0,0.35)",
    animation: "csSlideUp 0.28s cubic-bezier(0.22,1,0.36,1) both",
  },
  header: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    marginBottom: 22,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12 },
  mailIconWrap: {
    width: 40, height: 40, borderRadius: 10,
    background: "#F0E8D8",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  title:    { fontSize: 17, fontWeight: 700, color: "#1A1208" },
  sub:      { fontSize: 12, color: "#8B6A3A", marginTop: 1 },
  closeBtn: {
    width: 32, height: 32, borderRadius: "50%",
    border: "none", background: "#F0E8D8", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  field: { marginBottom: 14 },
  label: {
    fontSize: 11, fontWeight: 700, color: "#5C4423",
    letterSpacing: "0.06em", textTransform: "uppercase" as const,
    marginBottom: 6, display: "block",
  },
  input: {
    width: "100%", padding: "11px 14px", borderRadius: 12,
    border: "1.5px solid #D4B896", fontSize: 14, color: "#1A1208",
    outline: "none", fontFamily: "inherit", backgroundColor: "#FFF9F0",
    boxSizing: "border-box" as const,
  },
  textarea: {
    width: "100%", padding: "11px 14px", borderRadius: 12,
    border: "1.5px solid #D4B896", fontSize: 14, color: "#1A1208",
    outline: "none", fontFamily: "inherit", resize: "none" as const,
    backgroundColor: "#FFF9F0", lineHeight: "1.55",
    boxSizing: "border-box" as const,
  },
  sendBtn: {
    width: "100%", padding: "13px", borderRadius: 14, border: "none",
    background: "linear-gradient(135deg,#C8860A,#8B0000)",
    color: "#FFF9F0", fontSize: 15, fontWeight: 600, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    marginTop: 6, transition: "opacity 0.15s",
    boxSizing: "border-box" as const,
  },
  sentWrap: {
    display: "flex", flexDirection: "column", alignItems: "center",
    padding: "32px 0 16px", gap: 12,
  },
  sentIconWrap: {
    width: 56, height: 56, borderRadius: 16,
    background: "#F0E8D8",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  sentTitle: { fontSize: 18, fontWeight: 700, color: "#1A1208" },
  sentSub:   { fontSize: 14, color: "#8B6A3A" },
};

// ── Global CSS ────────────────────────────────────────────────
const CSS = `
  * { box-sizing: border-box; }

  @keyframes ringSpinCS {
    to { transform: rotate(360deg); }
  }
  @keyframes csFadeInUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes csSlideUp {
    from { transform: translateY(100%); opacity: 0; }
    to   { transform: translateY(0);   opacity: 1; }
  }
  @keyframes csBounce {
    0%, 80%, 100% { transform: translateY(0); }
    40%           { transform: translateY(-5px); }
  }

  .cs-msg-in, .cs-msg-out {
    animation: csFadeInUp 0.22s ease both;
  }
  .cs-dot {
    width: 7px; height: 7px; border-radius: 50%;
    background: #D1B48C;
    animation: csBounce 1.1s infinite ease-in-out;
    display: inline-block;
  }

  .cs-page {
    align-items: center;
    padding: 20px 12px;
  }
  .cs-shell {
    border-radius: 20px;
    min-height: unset;
    height: 680px;
    max-height: calc(100vh - 40px);
  }

  input:focus    { border-color: #C8860A !important; }
  textarea:focus { border-color: #C8860A !important; }
  ::-webkit-scrollbar { width: 0; }
`;

// ── Component styles ──────────────────────────────────────────
const S: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#1A1208",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px 12px",
    boxSizing: "border-box" as const,
    fontFamily: "'Inter','DM Sans',system-ui,sans-serif",
  },
  shell: {
    width: "100%",
    maxWidth: 480,
    height: 680,
    maxHeight: "calc(100vh - 40px)",
    backgroundColor: "#FFF9F0",
    borderRadius: 20,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 0 0 1px #C8860A33, 0 8px 64px rgba(0,0,0,0.65)",
  },
  header: {
    display: "flex", alignItems: "center", gap: 8,
    padding: "14px 16px",
    borderBottom: "1px solid #EAD7B8",
    backgroundColor: "#FFF9F0",
    position: "sticky" as const, top: 0, zIndex: 10,
    flexShrink: 0,
  },
  iconBtn: {
    width: 36, height: 36, borderRadius: "50%",
    border: "1.5px solid #EAD7B8", background: "#FFF9F0",
    display: "flex", alignItems: "center", justifyContent: "center",
    cursor: "pointer", flexShrink: 0,
  },
  headerName: {
    fontSize: 15, fontWeight: 700, color: "#1A1208", lineHeight: 1.2,
  },
  headerSub: {
    fontSize: 11, color: "#8B6A3A", marginTop: 2,
    display: "flex", alignItems: "center",
  },
  greetBanner: {
    display: "flex", alignItems: "center", gap: 8,
    margin: "12px 16px 0",
    padding: "10px 14px", borderRadius: 12,
    backgroundColor: "#FDF3E0",
    border: "1px solid #C8860A44",
    fontSize: 12, color: "#7A4F1A", fontWeight: 500,
    flexShrink: 0,
  },
  datePill: {
    alignSelf: "center",
    margin: "14px 0 6px",
    padding: "3px 14px", borderRadius: 99,
    backgroundColor: "#F0E8D8",
    fontSize: 11, color: "#8B6A3A", fontWeight: 500,
    flexShrink: 0,
  },
  messagesArea: {
    flex: 1, overflowY: "auto" as const,
    padding: "4px 16px 16px",
    display: "flex", flexDirection: "column" as const, gap: 12,
  },
  rowLeft: {
    display: "flex", alignItems: "flex-end", gap: 8,
    alignSelf: "flex-start", maxWidth: "82%",
  },
  rowRight: {
    display: "flex", justifyContent: "flex-end",
    alignSelf: "flex-end", maxWidth: "82%",
  },
  senderLabel: {
    fontSize: 11, fontWeight: 600, color: "#8B6A3A",
    marginBottom: 3, paddingLeft: 2,
  },
  bubbleOther: {
    backgroundColor: "#FFF9F0",
    border: "1px solid #EAD7B8",
    borderRadius: "4px 18px 18px 18px",
    padding: "10px 14px",
    display: "flex", flexDirection: "column" as const, gap: 4,
    boxShadow: "0 2px 8px rgba(200,134,10,0.1)",
  },
  bubbleMe: {
    background: "linear-gradient(135deg,#C8860A,#8B0000)",
    borderRadius: "18px 18px 4px 18px",
    padding: "10px 14px",
    display: "flex", flexDirection: "column" as const, gap: 4,
    boxShadow: "0 4px 18px rgba(200,134,10,0.35)",
  },
  bubbleText: {
    fontSize: 14, lineHeight: "1.55",
    wordBreak: "break-word" as const,
  },
  ts: {
    fontSize: 10, alignSelf: "flex-end" as const, opacity: 0.85,
  },
  typingBubble: {
    backgroundColor: "#FFF9F0",
    border: "1px solid #EAD7B8",
    borderRadius: "4px 18px 18px 18px",
    padding: "12px 16px",
    display: "flex", alignItems: "center", gap: 5,
    boxShadow: "0 2px 8px rgba(200,134,10,0.1)",
  },
  inputBar: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "12px 16px",
    borderTop: "1px solid #EAD7B8",
    backgroundColor: "#FFF9F0",
    flexShrink: 0,
  },
  inputWrap: { flex: 1 },
  input: {
    width: "100%",
    backgroundColor: "#F0E8D8",
    border: "1.5px solid transparent",
    borderRadius: 24, padding: "11px 18px",
    fontSize: 14, color: "#1A1208",
    outline: "none", fontFamily: "inherit",
    transition: "border-color 0.15s",
  },
  sendBtn: {
    width: 44, height: 44, borderRadius: "50%",
    border: "none", flexShrink: 0,
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "background 0.15s, transform 0.15s, box-shadow 0.15s",
  },
  poweredBy: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
    padding: "10px",
    fontSize: 11, color: "#B89060",
    flexShrink: 0,
  },
};